import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, EnvelopeSimple, Eye, EyeSlash, LockKey } from '@phosphor-icons/react';
import { useAuthStore } from '../../stores/authStore';
import { LoginRequest } from '../../types';
import AuthShell from './AuthShell';
import { AuthRedirectState, safeAuthReturnPath } from '../../hooks/useRequireAuth';

const LoginForm: React.FC = () => {
  const { login, isLoading, error, clearError } = useAuthStore();
  const { register, handleSubmit, formState: { errors } } = useForm<LoginRequest>();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const authState = location.state as AuthRedirectState | null;

  const onSubmit = async (data: LoginRequest) => {
    try {
      await login(data.email, data.password);
      navigate(safeAuthReturnPath(authState), { replace: true });
    } catch {
      // The auth store exposes the error to the form.
    }
  };

  React.useEffect(() => clearError(), [clearError]);

  return (
    <AuthShell eyebrow="Welcome back" title="Sign in to mapbl" description="Your map, people, and nearby moments are waiting.">
      <form className="space-y-5 rounded-[28px] border border-white/80 bg-white/85 p-5 shadow-[0_24px_80px_rgba(15,23,42,0.12)] backdrop-blur-xl dark:border-white/10 dark:bg-white/[0.055] sm:p-6" onSubmit={handleSubmit(onSubmit)}>
        {error && <div role="alert" className="rounded-2xl border border-pink-500/20 bg-pink-500/10 px-4 py-3 text-sm text-pink-700 dark:text-pink-200">{error}</div>}

        <div>
          <label htmlFor="email" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><EnvelopeSimple size={17} className="text-cyan-500" /> Email address</label>
          <input id="email" autoComplete="email" {...register('email', { required: 'Email is required', pattern: { value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, message: 'Enter a valid email address' } })} type="email" className={`w-full rounded-2xl border bg-slate-50 px-4 py-3.5 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/10 dark:bg-white/[0.06] dark:text-white ${errors.email ? 'border-pink-400' : 'border-slate-200 dark:border-white/10'}`} placeholder="you@example.com" />
          {errors.email && <p className="mt-1.5 text-xs text-pink-600 dark:text-pink-300">{errors.email.message}</p>}
        </div>

        <div>
          <label htmlFor="password" className="mb-2 flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-200"><LockKey size={17} className="text-violet-500" /> Password</label>
          <div className="relative">
            <input id="password" autoComplete="current-password" {...register('password', { required: 'Password is required', minLength: { value: 6, message: 'Password must be at least 6 characters' } })} type={showPassword ? 'text' : 'password'} className={`w-full rounded-2xl border bg-slate-50 px-4 py-3.5 pr-12 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-cyan-400 focus:ring-4 focus:ring-cyan-400/10 dark:bg-white/[0.06] dark:text-white ${errors.password ? 'border-pink-400' : 'border-slate-200 dark:border-white/10'}`} placeholder="Your password" />
            <button type="button" aria-label={showPassword ? 'Hide password' : 'Show password'} onClick={() => setShowPassword((value) => !value)} className="absolute right-2 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10">{showPassword ? <EyeSlash size={19} /> : <Eye size={19} />}</button>
          </div>
          {errors.password && <p className="mt-1.5 text-xs text-pink-600 dark:text-pink-300">{errors.password.message}</p>}
        </div>

        <button type="submit" disabled={isLoading} className="flex w-full items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-cyan-500 to-violet-500 px-4 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/20 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-50">
          {isLoading ? 'Signing in…' : 'Sign in'} {!isLoading && <ArrowRight size={17} weight="bold" />}
        </button>

        <button type="button" onClick={() => navigate('/register', { state: authState })} className="w-full text-center text-sm text-slate-500 transition hover:text-cyan-600 dark:text-slate-400 dark:hover:text-cyan-300">New to mapbl? <span className="font-semibold">Create an account</span></button>
      </form>
    </AuthShell>
  );
};

export default LoginForm;
